<script setup lang="ts">
import { ref, onMounted, provide, computed } from "vue";
import { apiClient } from "../api/client"; // circular: client.ts reads token that AuthProvider sets
import { JWT_SECRET } from "../config";
import type { User, AuthState } from "../types";

const user = ref<User | null>(null);
const token = ref<string | null>(null);

function decodeJWT(t: string): { sub: string; name: string; email: string; role: string } | null {
  try {
    const payload = t.split(".")[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

onMounted(() => {
  const stored = localStorage.getItem("vulntask_token");
  if (stored) {
    const decoded = decodeJWT(stored);
    if (decoded) {
      token.value = stored;
      user.value = {
        id: decoded.sub,
        email: decoded.email,
        name: decoded.name,
        avatar: `https://ui-avatars.com/api/?name=${decoded.name}`,
        role: decoded.role as User["role"],
      };
    }
  }
});

const signIn = async (email: string, password: string) => {
  const { data } = await apiClient.post("/api/v1/auth/login", { email, password });
  const newToken = data.data.token;
  localStorage.setItem("vulntask_token", newToken);
  const decoded = decodeJWT(newToken);
  token.value = newToken;
  if (decoded) {
    user.value = {
      id: decoded.sub,
      email: decoded.email,
      name: decoded.name,
      avatar: `https://ui-avatars.com/api/?name=${decoded.name}`,
      role: decoded.role as User["role"],
    };
  }
  // Debug: verify secret matches
  console.debug("[Auth] JWT_SECRET configured:", JWT_SECRET.substring(0, 20) + "...");
};

const signOut = () => {
  localStorage.removeItem("vulntask_token");
  user.value = null;
  token.value = null;
  window.location.href = "/login";
};

const isAuthenticated = computed(() => !!token.value && !!user.value);

const authState: AuthState = {
  get user() { return user.value; },
  get token() { return token.value; },
  get isAuthenticated() { return isAuthenticated.value; },
  signIn,
  signOut,
};

provide("auth", authState);
</script>

<template>
  <slot />
</template>
